// Helper centralizzato per le notifiche della Gruppo Impresa Suite.
//
// Questo modulo è il PUNTO UNICO in cui il frontend deriva metadati "ricchi"
// dalla forma minima del backend (`{id, kind, payload, actor_name, ...}`).
// L'obiettivo è che quando la Suite crescerà con altri moduli (Paghe, Pratiche,
// Contabilità, CRM, HR…) basterà estendere le mappe qui sotto senza toccare
// l'UI. La `Notification` server-side resta compatibile: severity/module/subject
// sono derivati, non nuovi campi DB.
//
// Convenzione `kind`:
//   `{modulo_snake_case}_{evento_snake_case}` — es. `presenze_balance_anomaly`,
//   `paghe_cedolino_pronto`, `pratiche_scadenza_documento`.
// Per retro-compat conserviamo i `kind` storici già emessi dal backend
// (attendance_submitted, balance_anomaly, ratei_ferie_alert, …).

import { MONTHS_IT } from "@/lib/constants";
import { SEVERITY_META, fmtRelative, fmtDateTimeFull } from "@/foundation/notifications";
import { fmtHours } from "@/foundation/formatting";

// Re-export Foundation utility per retrocompatibilita' dei consumer legacy.
export { fmtRelative, fmtDateTimeFull };

const KIND_SEVERITY = {
  // Modulo Presenze
  attendance_submitted:    "info",
  balance_anomaly:         "critical",
  ratei_ferie_alert:       "warning",
  ratei_rol_alert:         "warning",
  ratei_ex_fest_alert:     "warning",
  leave_request_submitted: "info",
  leave_request_approved:  "info",
  leave_request_rejected:  "warning",
  attendance_workflow_changed: "info",
  employee_profile_updated: "info",
  attendance_month_closed_external: "info",
  // Futuri moduli — placeholder documentato
  // paghe_cedolino_pronto:      "info",
  // pratiche_scadenza:          "warning",
  // pratiche_scadenza_critica:  "critical",
};

// ---------- Modulo di origine ----------
// Etichetta mostrata come "Modulo: Presenze" nel dettaglio. Utile quando la
// Suite includerà notifiche cross-modulo.
const KIND_MODULE = {
  attendance_submitted:    "Presenze",
  balance_anomaly:         "Presenze",
  ratei_ferie_alert:       "Presenze",
  ratei_rol_alert:         "Presenze",
  ratei_ex_fest_alert:     "Presenze",
  leave_request_submitted: "Presenze",
  leave_request_approved:  "Presenze",
  leave_request_rejected:  "Presenze",
  attendance_workflow_changed: "Presenze",
  employee_profile_updated: "Presenze",
  attendance_month_closed_external: "Presenze",
};

// ---------- Formatters ausiliari ----------
function fmt2(n) {
  if (n === null || n === undefined || isNaN(Number(n))) return "—";
  return Number(n).toFixed(2).replace(".", ",");
}
function fmt1(n) {
  if (n === null || n === undefined || isNaN(Number(n))) return "—";
  return Number(n).toFixed(1).replace(".", ",");
}
function meseAnno(p) {
  const y = p?.year;
  const m = p?.month;
  if (!y || !m) return "";
  return `${MONTHS_IT[m - 1]} ${y}`;
}

// NOTA · P2.0 STEP E1 (Opzione A · estrazione chirurgica):
// `fmtRelative` e `fmtDateTimeFull` sono stati promossi in Foundation
// (`@/foundation/notifications/formatters.js`) e re-esportati sopra da
// questo file. La logica sottostante e' stata rimossa da qui per evitare
// duplicazione (SSoT rispettata).

// ---------- Costruttori per singolo `kind` ----------
// Ognuno ritorna: { title, body, subject, detailFields:[{label,value}], cta:{label,to,testid}|null }

function buildAttendanceSubmitted(n) {
  const p = n.payload || {};
  const nome = n.actor_name || "Un dipendente";
  const ma = meseAnno(p);
  const empId = n.actor_employee_id || p.employee_id;
  return {
    title: `${nome} ha inviato le presenze`,
    body: `${ma} · ${p.count || 0} ${p.count === 1 ? "giorno" : "giorni"} da approvare`,
    subject: nome,
    detailFields: [
      { label: "Dipendente", value: nome },
      { label: "Periodo",    value: ma || "—" },
      { label: "Giorni",     value: String(p.count || 0) },
      { label: "Stato",      value: "In attesa di approvazione" },
    ],
    cta: empId && p.year && p.month ? {
      label: "Vai alla validazione",
      to: `/presenze?employee=${empId}&year=${p.year}&month=${p.month}`,
      testid: "notification-cta-validate",
    } : null,
  };
}

function buildBalanceAnomaly(n) {
  const p = n.payload || {};
  const nome = n.actor_name || "Un dipendente";
  const ma = meseAnno(p);
  const bal = fmtHours(p.balance);
  const thr = fmtHours(p.threshold ?? -20);
  const diff = fmtHours(Number(p.balance || 0) - Number(p.threshold ?? -20));
  const empId = n.actor_employee_id || p.employee_id;
  return {
    title: `Anomalia saldo: ${nome}`,
    body: `Saldo ${bal} h a ${ma} · soglia ${thr} h`,
    subject: nome,
    detailFields: [
      { label: "Dipendente",   value: nome },
      { label: "Periodo",      value: ma || "—" },
      { label: "Saldo attuale",value: `${bal} h` },
      { label: "Soglia allarme", value: `${thr} h` },
      { label: "Scostamento",  value: `${diff} h` },
    ],
    cta: empId && p.year && p.month ? {
      label: "Apri Gestione Presenze",
      to: `/presenze?employee=${empId}&year=${p.year}&month=${p.month}`,
      testid: "notification-cta-open-attendance",
    } : null,
  };
}

function buildRateiAlert(n) {
  const p = n.payload || {};
  const nome = n.actor_name || "Un dipendente";
  const pct = fmt1(p.percentage);
  const unit = p.unit || "h";
  const fmtQty = unit === "gg" ? fmt2 : fmtHours;
  const consumed = fmtQty(p.consumed);
  const total = fmtQty(p.total);
  const kindLabel = n.kind === "ratei_ferie_alert" ? "Ferie"
                   : n.kind === "ratei_rol_alert" ? "ROL / Permessi"
                   : "Ex Festività";
  return {
    title: `${kindLabel} in esaurimento (${pct}%)`,
    body: `${nome} · Consumato ${consumed} ${unit} su ${total} ${unit} nell'anno ${p.year || ""}`,
    subject: nome,
    detailFields: [
      { label: "Dipendente",    value: nome },
      { label: "Pool",          value: kindLabel },
      { label: "Anno",          value: String(p.year || "—") },
      { label: "Consumato",     value: `${consumed} ${unit}` },
      { label: "Disponibile",   value: `${total} ${unit}` },
      { label: "Percentuale",   value: `${pct} %` },
    ],
    cta: {
      label: "Apri Report annuo",
      to: "/report",
      testid: "notification-cta-open-report",
    },
  };
}

function buildLeaveRequest(n, phase) {
  const p = n.payload || {};
  const nome = n.actor_name || "Un dipendente";
  const from = p.date_from || "—";
  const to = p.date_to || "—";
  const cause = p.cause || "Assenza";
  const status = phase === "submitted" ? "In attesa di approvazione"
               : phase === "approved" ? "Approvata"
               : "Rifiutata";
  const title = phase === "submitted" ? `${nome} ha richiesto ${cause}`
              : phase === "approved" ? `Richiesta approvata: ${nome}`
              : `Richiesta rifiutata: ${nome}`;
  return {
    title,
    body: `Dal ${from} al ${to}`,
    subject: nome,
    detailFields: [
      { label: "Dipendente", value: nome },
      { label: "Causale",    value: cause },
      { label: "Dal",        value: from },
      { label: "Al",         value: to },
      { label: "Stato",      value: status },
    ],
    cta: {
      label: "Apri Calendario Assenze",
      to: "/calendario-ferie",
      testid: "notification-cta-open-calendar",
    },
  };
}

function buildWorkflowChanged(n) {
  const p = n.payload || {};
  const labels = {
    draft: "Bozza", sent: "Inviato", to_correct: "Da correggere",
    approved: "Approvato", closed: "Chiuso",
  };
  const nome = p.employee_name || n.actor_name || "Dipendente";
  const ma = meseAnno(p);
  return {
    title: `Stato cartellino aggiornato: ${nome}`,
    body: `${ma} · ${labels[p.from_state] || p.from_state || "—"} → ${labels[p.to_state] || p.to_state || "—"}`,
    subject: nome,
    detailFields: [
      { label: "Dipendente", value: nome },
      { label: "Azienda", value: p.company_name || "—" },
      { label: "Periodo", value: ma || "—" },
      { label: "Nuovo stato", value: labels[p.to_state] || p.to_state || "—" },
      { label: "Motivazione", value: p.reason || "—" },
    ],
    cta: n.actor_employee_id && p.year && p.month ? {
      label: "Apri Gestione Presenze",
      to: `/presenze?employee=${n.actor_employee_id}&year=${p.year}&month=${p.month}`,
      testid: "notification-cta-workflow",
    } : null,
  };
}

function buildProfileUpdated(n) {
  const p = n.payload || {};
  const nome = n.actor_name || "Dipendente";
  return {
    title: `Dati anagrafici aggiornati: ${nome}`,
    body: "La variazione è stata salvata correttamente.",
    subject: nome,
    detailFields: [
      { label: "Dipendente", value: nome },
      { label: "Campi aggiornati", value: (p.changed_fields || []).join(", ") || "Dati anagrafici" },
    ],
    cta: { label: "Apri Dipendenti", to: "/dipendenti", testid: "notification-cta-profile" },
  };
}

function buildExternalMonthClosed(n) {
  const p = n.payload || {};
  const nome = p.employee_name || "Dipendente";
  const ma = meseAnno(p);
  return {
    title: `Cartellino definitivamente chiuso: ${nome}`,
    body: `${ma}${p.company_name ? ` · ${p.company_name}` : ""}`,
    subject: nome,
    detailFields: [
      { label: "Dipendente", value: nome },
      { label: "Azienda", value: p.company_name || "—" },
      { label: "Periodo", value: ma || "—" },
      { label: "Stato", value: "Chiuso definitivamente" },
    ],
    cta: p.employee_id && p.year && p.month ? {
      label: "Apri Gestione Presenze",
      to: `/presenze?employee=${p.employee_id}&year=${p.year}&month=${p.month}`,
      testid: "notification-cta-external-closed",
    } : null,
  };
}

const KIND_BUILDERS = {
  attendance_submitted:    buildAttendanceSubmitted,
  balance_anomaly:         buildBalanceAnomaly,
  ratei_ferie_alert:       buildRateiAlert,
  ratei_rol_alert:         buildRateiAlert,
  ratei_ex_fest_alert:     buildRateiAlert,
  leave_request_submitted: (n) => buildLeaveRequest(n, "submitted"),
  leave_request_approved:  (n) => buildLeaveRequest(n, "approved"),
  leave_request_rejected:  (n) => buildLeaveRequest(n, "rejected"),
  attendance_workflow_changed: buildWorkflowChanged,
  employee_profile_updated: buildProfileUpdated,
  attendance_month_closed_external: buildExternalMonthClosed,
};

/**
 * Restituisce metadati COMPLETI di una notifica per la UI.
 * Se `kind` non è mappato, fallback generico con solo titolo/kind (retro-compat).
 */
export function getNotificationMeta(n) {
  if (!n) return null;
  const severityKey = KIND_SEVERITY[n.kind] || "info";
  const severity = { key: severityKey, ...SEVERITY_META[severityKey] };
  const module = KIND_MODULE[n.kind] || "Suite";
  const builder = KIND_BUILDERS[n.kind];
  const built = builder ? builder(n) : {
    title: "Notifica di sistema",
    body: n.kind,
    subject: "—",
    detailFields: [{ label: "Tipo evento", value: n.kind }],
    cta: null,
  };
  return {
    id: n.id,
    kind: n.kind,
    isRead: !!n.is_read,
    createdAt: n.created_at,
    severity,
    module,
    ...built,
  };
}

// Etichette dei filtri predisposti (Archiviate nascosto per ora, backend
// dovrà supportarlo con `archived_at` in una futura migration additiva).
export const NOTIFICATION_TABS = [
  { key: "unread",  label: "Non lette" },
  { key: "recent",  label: "Recenti"   },
  // { key: "archived", label: "Archiviate" }, // ← TODO Suite v1.1 · richiede endpoint archive
];
